'use client';

import { useMemo, type ReactNode } from 'react';
import { createInstance, type i18n, type Resource } from 'i18next';
import { I18nextProvider } from 'react-i18next';
import { locales as blockLocales } from '@akademi/site-builder-blocks/locales';
import { locales as commerceBlockLocales } from '@commerce/site-builder-blocks/locales';
import { locales as sorsyThemeLocales } from '@commerce/theme-sorsy/locales';
import { locales as blocksCommonLocales } from '@/src/locales/blocksCommon';

/**
 * The translation catalogue every site-builder block reads through.
 *
 * <h3>What was wrong</h3>
 *
 * <p>This host mounted no i18next instance at all. Every block calls
 * `useTranslation('site-builder-blocks')`, and with no instance in context
 * i18next's documented behaviour is to return the key — so a shopper saw
 * `commerce.cart.continue` where "Continue shopping" belongs, and
 * `commerce.checkout.name` where "Full name" belongs. It was in the production
 * build as much as in dev, on every block on every page, and it is the single
 * largest reason the storefront did not read like a finished shop.
 *
 * <p>The strings were never missing. `@akademi/site-builder-blocks/locales` has
 * shipped them the whole time, in both languages; nothing was mounted to look
 * them up. Only props the operator had authored appeared, because those come
 * through `tx()` — which is why the pages looked half-translated rather than
 * obviously broken, and why it survived this long.
 *
 * <h3>Fragments</h3>
 *
 * <p>Three catalogues make it up: `@akademi/site-builder-blocks/locales` for the
 * generic blocks, `@commerce/site-builder-blocks/locales` for the shop's blocks,
 * and `@commerce/theme-sorsy/locales` for the Sorsy theme, whose enquiry block
 * reads its own `theme-sorsy` namespace and showed its keys while only the first
 * was loaded here.
 *
 * <p>The catalogue arrives as a list of `{ ns, resources }` fragments and
 * several share a namespace — the blocks package splits its own across
 * `main`, `main2` and `main3` so that parallel work never touched one file.
 * They must therefore be **merged**, not assigned: a later fragment replacing
 * `site-builder-blocks` wholesale would drop every key the earlier ones hold.
 * The merge is one level deep by design — fragments partition the key space
 * rather than overlapping inside it.
 *
 * <h3>Language</h3>
 *
 * <p>Resources are keyed by language (`en`, `tr`) while a tenant's locale is a
 * full BCP 47 tag (`en-GB`), so the subtag is what is asked for. `fallbackLng`
 * catches a tenant configured for a language this build does not ship, which
 * renders English rather than keys — wrong, but readable, which is the whole
 * point of the exercise.
 */

const FALLBACK_LANGUAGE = 'en';

// blocksCommonLocales: the portal blocks' `common` namespace, in English (see that file).
const FRAGMENTS = [...blockLocales, ...commerceBlockLocales, ...sorsyThemeLocales, ...blocksCommonLocales];

/** Built instances, keyed by language. Initialising i18next is not free and the
 *  server does it once per render; a shop has one or two languages, not many. */
const instances = new Map<string, i18n>();

function buildResources(): Resource {
  const resources: Resource = {};
  for (const fragment of FRAGMENTS) {
    for (const [language, bundle] of Object.entries(fragment.resources)) {
      const forLanguage = (resources[language] ??= {});
      // Merge rather than assign — see the note on fragments above. The cast is
      // because i18next types a namespace as `string | object`; every fragment
      // here is an object, and a string one would be a malformed catalogue.
      const existing = (forLanguage[fragment.ns] ?? {}) as Record<string, unknown>;
      forLanguage[fragment.ns] = { ...existing, ...(bundle as Record<string, unknown>) };
    }
  }
  return resources;
}

function instanceFor(language: string): i18n {
  const existing = instances.get(language);
  if (existing) return existing;

  const resources = buildResources();
  const instance = createInstance();
  // No `use(initReactI18next)`: that would also install this as react-i18next's
  // global default, and this instance belongs to the subtree below rather than
  // to the whole app. `I18nextProvider` supplies it through context instead.
  //
  // Synchronous because every resource is already in memory — there is no
  // loader plugin — so `t()` works on the first render rather than after a
  // round trip that server rendering would never wait for.
  void instance.init({
    resources,
    lng: language,
    fallbackLng: FALLBACK_LANGUAGE,
    ns: Object.keys(resources[language] ?? resources[FALLBACK_LANGUAGE] ?? {}),
    defaultNS: 'site-builder-blocks',
    interpolation: {
      // React escapes for us, and double-escaping turns an apostrophe in
      // "Your basket's empty" into `&#39;` on the page.
      escapeValue: false,
    },
    returnNull: false,
    // A key with no translation should render the key, which is what makes a
    // missing string obvious in review rather than silently blank.
    parseMissingKeyHandler: (key) => key,
  });
  instances.set(language, instance);
  return instance;
}

export function BlocksI18nProvider({
  locale,
  children,
}: {
  /** The tenant's locale, a full BCP 47 tag. */
  locale: string;
  children: ReactNode;
}) {
  const language = (locale || FALLBACK_LANGUAGE).split('-')[0].toLowerCase();
  const instance = useMemo(() => instanceFor(language), [language]);
  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
}
